import { type ChangeEvent, type FormEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ArrowUpDown, Camera, Check, Plus, Save, Trash2, X } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useDispatch } from 'react-redux';

import { Input } from '../../../components/ui/input';
import { WeekRangeDatePicker } from '../../../components/ui/WeekRangeDatePicker';
import { Textarea } from '../../../components/ui/textarea';
import Toast from '../../../components/Toast/Toast';
import { PhotoImage } from '../../../components/common/PhotoImage';
import UnsavedChangesDialog from '../../../components/common/UnsavedChangesDialog';
import ApprovalStatusBadge from '../../../components/common/ApprovalStatusBadge';
import { useAppSelector } from '../../../store';
import {
  setProfileIncomplete,
  updateBackgroundInfo,
  updatePersonalInfo,
  updateProfessionalInfo,
} from '../../../redux/slices/userProfileSlice';
import {
  Admin_ExploreTechnologies_LIST,
  Admin_GetUserDetail_LIST,
  Admin_ITSpecialty_LIST,
  Admin_Skills_LIST,
  UserProfile_GetByUserId,
  getAvailabilityTypes,
  getUserAvailability,
} from '../../../constants/adminData';
import { useSaveUserProfile } from '../../../hooks/useSaveUserProfile';
import { useUnsavedChangesGuard } from '../../../hooks/useUnsavedChangesGuard';
import { getSensitiveData } from '../../../utils/secureStorage';
import type { ExploreTechnologies, ITSpecialty, Skill } from '../../../types/admin';
import { useTheme } from '@/context/ThemeContext';

interface SkillWithExperience {
  skillId: number;
  skillName: string;
  experience: number;
  approvalStatus?: string | null;
  rejectionReason?: string | null;
}

interface SelectedExploreTechnology {
  interestId: number;
  interestName: string;
  customExploreTechnologies?: string;
}

interface AvailabilityTypeOption {
  availabilityTypeId: number;
  availabilityTypeName: string;
}

interface WeeklyAvailability {
  startDate: string;
  endDate: string;
  availableHoursPerWeek: string;
}

interface AvailabilityException extends WeeklyAvailability {
  id: string;
  availabilityExceptionId: number;
}

interface AvailabilityFormState {
  availabilityId: number;
  isAvailable: boolean;
  availabilityTypeIds: number[];
  approvalStatus: string | null;
}

const emptyAvailabilityForm: AvailabilityFormState = {
  availabilityId: 0,
  isAvailable: true,
  availabilityTypeIds: [],
  approvalStatus: null,
};

const getResponseData = (response: any) => response?.data ?? response;

const toDateInputValue = (value?: string | null) => {
  if (!value) return '';
  return String(value).slice(0, 10);
};

const parseDateInput = (value: string) => {
  const [year, month, day] = value.split('-').map(Number);
  if (!year || !month || !day) return null;
  return new Date(year, month - 1, day);
};

const formatDateInput = (date: Date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const addDays = (date: Date, days: number) => {
  const nextDate = new Date(date);
  nextDate.setDate(nextDate.getDate() + days);
  return nextDate;
};

const getMonday = (value: string | Date) => {
  const date = typeof value === 'string' ? parseDateInput(value) : value;
  if (!date) return '';
  const day = date.getDay();
  const distanceFromMonday = day === 0 ? -6 : 1 - day;
  return formatDateInput(addDays(date, distanceFromMonday));
};

const getSunday = (value: string | Date) => {
  const monday = parseDateInput(getMonday(value));
  if (!monday) return '';
  return formatDateInput(addDays(monday, 6));
};

const isMonday = (value: string) => parseDateInput(value)?.getDay() === 1;

const isSunday = (value: string) => parseDateInput(value)?.getDay() === 0;

const getNextMonday = (value: string | Date) => {
  const date = typeof value === 'string' ? parseDateInput(value) : value;
  if (!date) return '';
  const nextDate = addDays(date, 1);
  const day = nextDate.getDay();
  const distanceToMonday = day === 0 ? 1 : (8 - day) % 7;
  return formatDateInput(addDays(nextDate, distanceToMonday));
};

const getCurrentWeek = (): WeeklyAvailability => {
  const startDate = getMonday(new Date());
  return {
    startDate,
    endDate: getSunday(startDate),
    availableHoursPerWeek: '',
  };
};

const normalizeHours = (value: string) => {
  if (value === '') return '';
  const nextValue = Math.max(0, Math.min(40, Number(value)));
  return Number.isFinite(nextValue) ? String(nextValue) : '';
};

const getTypeId = (item: any): number | null => {
  const rawValue = item?.availabilityTypeId ?? item?.id;
  const id = Number(rawValue);
  return Number.isFinite(id) && id > 0 ? id : null;
};

const getTypeName = (item: any): string => item?.availabilityTypeName ?? item?.name ?? item?.label ?? '';

const normalizeAvailabilityTypes = (response: any): AvailabilityTypeOption[] => {
  const data = getResponseData(response);
  const items = Array.isArray(data) ? data : [];
  return items
    .map((item) => {
      const availabilityTypeId = getTypeId(item);
      if (!availabilityTypeId) return null;
      return {
        availabilityTypeId,
        availabilityTypeName: getTypeName(item),
      };
    })
    .filter(Boolean) as AvailabilityTypeOption[];
};

const normalizeExceptions = (value: any): AvailabilityException[] => {
  const items = Array.isArray(value) ? value : [];
  return items.map((item: any, index: number) => ({
    id: Number(item?.availabilityExceptionId ?? 0) > 0 ? String(item.availabilityExceptionId) : `temp-${index + 1}`,
    availabilityExceptionId: Number(item?.availabilityExceptionId ?? 0) || 0,
    startDate: toDateInputValue(item?.startDate),
    endDate: toDateInputValue(item?.endDate),
    availableHoursPerWeek:
      item?.availableHoursPerWeek || item?.availableHoursPerWeek === 0 ? String(item.availableHoursPerWeek) : '',
  }));
};

const normalizeAvailability = (value: any) => {
  const data = getResponseData(value);
  const source = Array.isArray(data) ? data[0] : data;
  const rows = Array.isArray(data) ? data : Array.isArray(source?.availabilities) ? source.availabilities : [];

  if (!source && rows.length === 0) return null;

  const availabilityTypes = Array.isArray(source?.availabilityTypes) ? source.availabilityTypes : rows;
  const availabilityTypeIds =
    Array.isArray(source?.availabilityTypeIds) && source.availabilityTypeIds.length > 0
      ? source.availabilityTypeIds.map(Number).filter((id: number) => Number.isFinite(id) && id > 0)
      : Array.isArray(data) && data.length > 1
        ? data.map((item: any) => getTypeId(item)).filter((id: number | null): id is number => id !== null)
        : getTypeId(source)
          ? [getTypeId(source) as number]
          : availabilityTypes
              .map((item: any) => getTypeId(item))
              .filter((id: number | null): id is number => id !== null);

  const firstRow = rows[0] ?? source;
  const defaultStartDate = toDateInputValue(
    source?.defaultStartDate ?? firstRow?.defaultStartDate ?? source?.availableFromDate ?? firstRow?.availableFromDate,
  );
  const defaultEndDate = toDateInputValue(
    source?.defaultEndDate ?? firstRow?.defaultEndDate ?? source?.availableToDate ?? firstRow?.availableToDate,
  );
  const defaultAvailableHoursPerWeek =
    source?.defaultAvailableHoursPerWeek ??
    firstRow?.defaultAvailableHoursPerWeek ??
    source?.availableHoursPerWeek ??
    firstRow?.availableHoursPerWeek;

  return {
    form: {
      availabilityId: Number(source?.availabilityId ?? firstRow?.availabilityId ?? 0) || 0,
      isAvailable: Boolean(source?.isAvailable ?? firstRow?.isAvailable ?? true),
      availabilityTypeIds,
      approvalStatus: source?.approvalStatus ?? firstRow?.approvalStatus ?? null,
    },
    defaultAvailability: {
      startDate: defaultStartDate || getCurrentWeek().startDate,
      endDate: defaultEndDate || getSunday(defaultStartDate || getCurrentWeek().startDate),
      availableHoursPerWeek:
        defaultAvailableHoursPerWeek || defaultAvailableHoursPerWeek === 0 ? String(defaultAvailableHoursPerWeek) : '',
    },
    exceptions: normalizeExceptions(source?.exceptions ?? firstRow?.exceptions),
    // Any additional availability rows beyond the one edited in this form must be
    // preserved and re-sent on save, or the backend's full-replace sync deletes them.
    otherRows: rows.slice(1),
  };
};

const rangesOverlap = (first: AvailabilityException, second: AvailabilityException) => {
  const firstStart = parseDateInput(first.startDate)?.getTime() ?? 0;
  const firstEnd = parseDateInput(first.endDate)?.getTime() ?? 0;
  const secondStart = parseDateInput(second.startDate)?.getTime() ?? 0;
  const secondEnd = parseDateInput(second.endDate)?.getTime() ?? 0;
  return firstStart <= secondEnd && secondStart <= firstEnd;
};

const hasOverlappingExceptions = (exceptions: AvailabilityException[]) => {
  for (let index = 0; index < exceptions.length; index += 1) {
    for (let nextIndex = index + 1; nextIndex < exceptions.length; nextIndex += 1) {
      if (rangesOverlap(exceptions[index], exceptions[nextIndex])) return true;
    }
  }
  return false;
};

const getInputClassName = ({
  hasError,
  startDate,
  endDate,
  isEndField = false,
}: {
  hasError: boolean;
  startDate: string;
  endDate: string;
  isEndField?: boolean;
}) => {
  // Shared architectural baseline layout styles
  const baseClass =
    'w-full rounded-lg px-3 py-2.5 text-sm outline-none transition disabled:cursor-not-allowed disabled:bg-gray-50 disabled:text-gray-400 disabled:border-gray-200';

  if (hasError) {
    return `${baseClass} border border-red-500 bg-white focus:border-red-500 focus:ring-2 focus:ring-red-500/20`;
  }

  if (!startDate) {
    return `${baseClass} border border-gray-300 bg-white focus:border-brand-primary focus:ring-2 focus:ring-brand-primary/20`;
  }

  const isRangeActive = endDate && endDate !== getSunday(startDate);
  const shouldHighlight = !isEndField || isRangeActive;

  if (shouldHighlight) {
    // Isolated Highlight State (Light Blue theme applied across normal & active focus selection)
    return `${baseClass} border border-blue-400 bg-blue-50 ring-2 ring-blue-400/20 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/30`;
  }

  // Standard Form State
  return `${baseClass} border border-gray-300 bg-white focus:border-brand-primary focus:ring-2 focus:ring-brand-primary/20`;
};

const getStartDateTime = (value: string) => {
  const parsed = parseDateInput(value);
  return parsed ? parsed.getTime() : Number.POSITIVE_INFINITY;
};

const fieldClass =
  'w-full rounded-lg border border-gray-300 bg-white px-3 py-2.5 text-sm outline-none transition focus:border-brand-primary focus:ring-2 focus:ring-brand-primary/20 disabled:cursor-not-allowed disabled:bg-gray-50 disabled:text-gray-400 disabled:border-gray-200';

function FieldError({ message }: { readonly message?: string }) {
  if (!message) return null;
  return <p className="mt-1 text-xs font-medium text-red-600">{message}</p>;
}

function RequiredMark() {
  return <span className="text-red-500"> *</span>;
}

export default function CompleteProfileForm() {
   const { theme } = useTheme();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const routeLocation = useLocation();
  const profile = useAppSelector((state) => state.userProfile);
  const { saveUserProfile, toast, clearToast } = useSaveUserProfile();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [specialties, setSpecialties] = useState<ITSpecialty[]>([]);
  const [skills, setSkills] = useState<Skill[]>([]);
  const [technologies, setTechnologies] = useState<ExploreTechnologies[]>([]);
  const [availabilityTypes, setAvailabilityTypes] = useState<AvailabilityTypeOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [formError, setFormError] = useState('');
  const [availabilityFocused, setAvailabilityFocused] = useState(false);

  const [firstName, setFirstName] = useState(profile.personalInfo?.firstName || '');
  const [lastName, setLastName] = useState(profile.personalInfo?.lastName || '');
  const [profileImage, setProfileImage] = useState<string | null>(profile.personalInfo?.profileImage || null);
  const [profileImageFile, setProfileImageFile] = useState<File | null>(profile.personalInfo?.profileImageFile || null);
  const [photoId, setPhotoId] = useState(profile.personalInfo?.photoId ? String(profile.personalInfo.photoId) : '');
  const [currentOrganization, setCurrentOrganization] = useState(profile.professionalInfo?.currentOranizationName || '');
  const [location, setLocation] = useState(profile.professionalInfo?.location?.displayName || '');
  const [itSpecialtyId, setItSpecialtyId] = useState(profile.professionalInfo?.itSpecialtyId ? String(profile.professionalInfo.itSpecialtyId) : '');
  const [totalExperience, setTotalExperience] = useState(
    profile.professionalInfo?.totalExperience ? String(profile.professionalInfo.totalExperience) : '0',
  );
  const [organizationCount, setOrganizationCount] = useState(
    profile.professionalInfo?.organizationCount ? String(profile.professionalInfo.organizationCount) : '',
  );
  const [shortBio, setShortBio] = useState(profile.backgroundInfo?.shortBio || '');
  const [selectedSkills, setSelectedSkills] = useState<SkillWithExperience[]>(
    Array.isArray(profile.backgroundInfo?.skills) ? profile.backgroundInfo.skills : [],
  );
  const [rejectionReasonModalSkill, setRejectionReasonModalSkill] = useState<SkillWithExperience | null>(null);
  const [selectedTechnologies, setSelectedTechnologies] = useState<SelectedExploreTechnology[]>([]);
  const [availabilityForm, setAvailabilityForm] = useState<AvailabilityFormState>(emptyAvailabilityForm);
  const [defaultAvailability, setDefaultAvailability] = useState<WeeklyAvailability>(getCurrentWeek);
  const [exceptions, setExceptions] = useState<AvailabilityException[]>([]);
  const otherAvailabilitiesRef = useRef<any[]>([]);
  // Keep the saved snapshots in state rather than refs. A ref update after a
  // save does not re-render the form, leaving the unsaved-changes guard with a
  // stale value on the next navigation attempt.
  const [initialSnapshot, setInitialSnapshot] = useState<string | null>(null);
  const [initialAvailabilitySnapshot, setInitialAvailabilitySnapshot] = useState<string | null>(null);

  const [exceptionSortDirection, setExceptionSortDirection] = useState<'asc' | 'desc'>('desc');

  const currentWeekStart = useMemo(() => getMonday(new Date()), []);

  // Past exceptions stay in state (and get re-sent on save) so save doesn't
  // silently delete history, but they're hidden from the editable list.
  const activeExceptions = useMemo(
    () => exceptions.filter((exception) => !exception.endDate || exception.endDate >= currentWeekStart),
    [exceptions, currentWeekStart],
  );

  useEffect(() => {
    let ignore = false;

    const loadOwnProfileIfNeeded = async () => {
      if (profile.professionalInfo?.itSpecialtyId) return null;

      try {
        const userId = profile.userId || (await getSensitiveData('userId')) || '';
        const authToken = profile.authToken || (await getSensitiveData('authToken')) || '';
        if (!userId || !authToken) return null;

        const [userDetailResponse, profileResponse] = await Promise.all([
          Admin_GetUserDetail_LIST(userId, authToken),
          UserProfile_GetByUserId(userId),
        ]);
        if (!profileResponse?.success) return null;

        return {
          userProfileData: profileResponse.data,
          userData: userDetailResponse?.success ? userDetailResponse.data : null,
        };
      } catch (error) {
        console.error('Failed to load own profile for complete-profile form:', error);
        return null;
      }
    };

    const loadMasterData = async () => {
      try {
        const [
          specialtiesResponse,
          skillsResponse,
          technologiesResponse,
          availabilityTypesResponse,
          availabilityResponse,
          ownProfile,
        ] = await Promise.all([
          Admin_ITSpecialty_LIST(),
          Admin_Skills_LIST(),
          Admin_ExploreTechnologies_LIST(),
          getAvailabilityTypes(),
          getUserAvailability().catch(() => null),
          loadOwnProfileIfNeeded(),
        ]);

        if (ignore) return;

        const safeSpecialties = Array.isArray(specialtiesResponse?.data)
          ? specialtiesResponse.data
          : Array.isArray(specialtiesResponse)
            ? specialtiesResponse
            : [];
        const safeSkills = Array.isArray(skillsResponse)
          ? skillsResponse
          : Array.isArray(skillsResponse?.data)
            ? skillsResponse.data
            : [];
        const safeTechnologies = Array.isArray(technologiesResponse)
          ? technologiesResponse
          : Array.isArray(technologiesResponse?.data)
            ? technologiesResponse.data
            : [];

        setSpecialties(safeSpecialties);
        setSkills(safeSkills);
        setTechnologies(safeTechnologies);
        setAvailabilityTypes(normalizeAvailabilityTypes(availabilityTypesResponse));

        if (ownProfile) {
          const { userProfileData, userData } = ownProfile;

          dispatch(
            updatePersonalInfo({
              firstName: userProfileData.firstName,
              lastName: userProfileData.lastName,
              profileImage: userProfileData.profileImageUrl || undefined,
              photoId: userData?.photoId || undefined,
            }),
          );
          dispatch(
            updateProfessionalInfo({
              itSpecialtyId: userProfileData.itspecialtyId,
              currentOranizationName: userProfileData.currentOranizationName,
              totalExperience: userProfileData.totalExperienceInYears,
              organizationCount: userProfileData.totalCompaniesHaveBeen,
              location: userProfileData.currentLocation
                ? { displayName: userProfileData.currentLocation, latitude: 0, longitude: 0 }
                : undefined,
              availabilities: userProfileData.availabilities,
            }),
          );
          dispatch(
            updateBackgroundInfo({
              shortBio: userProfileData.shortBio || '',
              skills: userProfileData.skills,
              interests: userProfileData.interests,
            }),
          );

          setFirstName(userProfileData.firstName || '');
          setLastName(userProfileData.lastName || '');
          setProfileImage(userProfileData.profileImageUrl || null);
          setPhotoId(userData?.photoId ? String(userData.photoId) : '');
          setCurrentOrganization(userProfileData.currentOranizationName || '');
          setLocation(userProfileData.currentLocation || '');
          setItSpecialtyId(userProfileData.itspecialtyId ? String(userProfileData.itspecialtyId) : '');
          setTotalExperience(
            userProfileData.totalExperienceInYears ? String(userProfileData.totalExperienceInYears) : '0',
          );
          setOrganizationCount(
            userProfileData.totalCompaniesHaveBeen ? String(userProfileData.totalCompaniesHaveBeen) : '',
          );
          setShortBio(userProfileData.shortBio || '');
          setSelectedSkills(Array.isArray(userProfileData.skills) ? userProfileData.skills : []);
        }

        const savedInterests = ownProfile
          ? Array.isArray(ownProfile.userProfileData.interests)
            ? ownProfile.userProfileData.interests
            : []
          : Array.isArray(profile.backgroundInfo?.interests)
            ? profile.backgroundInfo.interests
            : [];
        setSelectedTechnologies(
          savedInterests
            .map((item: any) => {
              const interestId = item.interestId ?? item.id;
              const matched = safeTechnologies.find((technology: ExploreTechnologies) => technology.interestId === interestId);
              if (!matched) return null;
              return {
                interestId: matched.interestId,
                interestName: matched.interestName,
                customExploreTechnologies: item.customExploreTechnologies ?? '',
              };
            })
            .filter(Boolean) as SelectedExploreTechnology[],
        );

        const normalizedAvailability =
          normalizeAvailability(availabilityResponse) ??
          normalizeAvailability(ownProfile?.userProfileData.availabilities) ??
          normalizeAvailability(profile.professionalInfo?.availabilities) ??
          normalizeAvailability(JSON.parse((await getSensitiveData('userData')) || '{}')?.availabilities);

        if (normalizedAvailability) {
          setAvailabilityForm(normalizedAvailability.form);
          setDefaultAvailability(normalizedAvailability.defaultAvailability);
          setExceptions(normalizedAvailability.exceptions);
          otherAvailabilitiesRef.current = normalizedAvailability.otherRows ?? [];
        }
      } catch (error) {
        console.error('Failed to load profile form data:', error);
        setFormError('Unable to load profile details.');
      } finally {
        if (!ignore) setLoading(false);
      }
    };

    loadMasterData();

    return () => {
      ignore = true;
    };
  }, []);

  useEffect(() => {
    const requestedSection = (routeLocation.state as { section?: string } | null)?.section;
    if ((routeLocation.pathname === '/IT-AvailabilityType' || requestedSection === 'availability') && !loading) {
      const timer = setTimeout(() => {
        const element = document.getElementById('availability-section');
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
          element.focus({ preventScroll: true });
          setAvailabilityFocused(true);
          setTimeout(() => setAvailabilityFocused(false), 2000);
        }
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [routeLocation.pathname, routeLocation.state, loading]);

  const selectedAvailabilityTypes = useMemo(
    () => availabilityTypes.filter((type) => availabilityForm.availabilityTypeIds.includes(type.availabilityTypeId)),
    [availabilityForm.availabilityTypeIds, availabilityTypes],
  );

  const errors = useMemo(() => {
    const nextErrors: Record<string, string> = {};
    if (!firstName.trim()) nextErrors.firstName = 'First name is required.';
    if (!lastName.trim()) nextErrors.lastName = 'Last name is required.';
    if (!currentOrganization.trim()) nextErrors.currentOrganization = 'Current organization is required.';
    if (!location.trim()) nextErrors.location = 'Location is required.';
    if (!itSpecialtyId) nextErrors.itSpecialtyId = 'IT specialty is required.';
    if (selectedSkills.length === 0) nextErrors.selectedSkills = 'At least one skill is required.';
    if (selectedTechnologies.length === 0) nextErrors.selectedTechnologies = 'At least one technology to explore is required.';
    if (totalExperience === '') nextErrors.totalExperience = 'Total experience is required.';
    if (organizationCount === '') nextErrors.organizationCount = 'Number of companies is required.';  

    const totalExperienceValue = Number(totalExperience);
    if (totalExperience && (!Number.isFinite(totalExperienceValue) || totalExperienceValue < 0)) {
      nextErrors.totalExperience = 'Total experience must be a non-negative number.';
    }

    const organizationCountValue = Number(organizationCount);
    if (organizationCount && (!Number.isFinite(organizationCountValue) || organizationCountValue < 0)) {
      nextErrors.organizationCount = 'Number of companies must be a non-negative number.';
    }

    if (availabilityForm.isAvailable) {
      const defaultHours = Number(defaultAvailability.availableHoursPerWeek);
      if (defaultAvailability.availableHoursPerWeek === '') {
        nextErrors.defaultHours = 'Default hours per week is required.';
      } else if (!Number.isFinite(defaultHours) || defaultHours < 0 || defaultHours > 40) {
        nextErrors.defaultHours = 'Default hours must be between 0 and 40.';
      }
    }

    activeExceptions.forEach((exception, index) => {
      const start = parseDateInput(exception.startDate);
      const end = parseDateInput(exception.endDate);
      const hours = Number(exception.availableHoursPerWeek);
      if (!exception.startDate) nextErrors[`exception-${exception.id}-startDate`] = 'Start date is required.';
      if (!exception.endDate) nextErrors[`exception-${exception.id}-endDate`] = 'End date is required.';
      if (exception.startDate && !isMonday(exception.startDate)) {
        nextErrors[`exception-${exception.id}-startDate`] = 'Start date must be a Monday.';
      }
      if (exception.startDate && exception.startDate < currentWeekStart) {
        nextErrors[`exception-${exception.id}-startDate`] = 'Start date cannot be in the past.';
      }
      if (exception.endDate && !isSunday(exception.endDate)) {
        nextErrors[`exception-${exception.id}-endDate`] = 'End date must be a Sunday.';
      }
      if (start && end && end < start) nextErrors[`exception-${exception.id}-endDate`] = 'End date must be after start date.';
      if (exception.availableHoursPerWeek === '') {
        nextErrors[`exception-${exception.id}-hours`] = 'Hours are required.';
      } else if (!Number.isFinite(hours) || hours < 0 || hours > 40) {
        nextErrors[`exception-${exception.id}-hours`] = 'Hours must be between 0 and 40.';
      }
      if (index > 0 && hasOverlappingExceptions(activeExceptions)) {
        nextErrors.exceptions = 'Exception date ranges cannot overlap.';
      }
    });

    return nextErrors;
  }, [activeExceptions, availabilityForm.isAvailable, currentOrganization, currentWeekStart, defaultAvailability, firstName, itSpecialtyId, lastName, location, organizationCount, selectedSkills, selectedTechnologies, totalExperience]);

  const isFormValid = Object.keys(errors).length === 0;

  const MAX_PROFILE_PHOTO_SIZE_BYTES = 5 * 1024 * 1024;

  const readSelectedPhoto = (file: File) => {
    setProfileImageFile(file);
    const reader = new FileReader();
    reader.onloadend = () => setProfileImage(reader.result as string);
    reader.readAsDataURL(file);
  };

  const handlePhotoChange = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      setFormError('Please select a valid image file.');
      return;
    }

    if (file.size > MAX_PROFILE_PHOTO_SIZE_BYTES) {
      setFormError('Profile photo must be under 5MB. Please choose a smaller image.');
      return;
    }

    setFormError('');
    readSelectedPhoto(file);
  };

  const toggleSkill = (skillId: string) => {
    const numericId = Number(skillId);
    const skill = skills.find((item) => item.skillId === numericId);
    if (!skill) return;

    setSelectedSkills((current) =>
      current.some((item) => item.skillId === numericId)
        ? current.filter((item) => item.skillId !== numericId)
        : [...current, { skillId: skill.skillId, skillName: skill.skillName, experience: 0 }],
    );
  };

  const updateSkillExperience = (skillId: number, experience: string) => {
    const numericExperience = Math.max(0, Number(experience) || 0);
    setSelectedSkills((current) =>
      current.map((item) => (item.skillId === skillId ? { ...item, experience: numericExperience } : item)),
    );
  };

  const toggleTechnology = (interestId: string) => {
    const numericId = Number(interestId);
    const technology = technologies.find((item) => item.interestId === numericId);
    if (!technology) return;

    setSelectedTechnologies((current) =>
      current.some((item) => item.interestId === numericId)
        ? current.filter((item) => item.interestId !== numericId)
        : [...current, { interestId: technology.interestId, interestName: technology.interestName, customExploreTechnologies: '' }],
    );
  };

  const toggleAvailabilityType = (availabilityTypeId: number) => {
    if (!availabilityForm.isAvailable) return;
    setAvailabilityForm((current) => ({
      ...current,
      availabilityTypeIds: current.availabilityTypeIds.includes(availabilityTypeId)
        ? current.availabilityTypeIds.filter((id) => id !== availabilityTypeId)
        : [...current.availabilityTypeIds, availabilityTypeId],
    }));
  };

  const setAvailabilityStatus = (isAvailable: boolean) => {
    setAvailabilityForm((current) => {
      // Clicking the selected radio option is not an availability change.
      if (current.isAvailable === isAvailable) return current;

      // Keep the selected types so switching back to Available restores them;
      // the save payload drops them while Not Available.
      return { ...current, isAvailable };
    });
  };

  const addException = () => {
    const lastException = activeExceptions[activeExceptions.length - 1];
    const seedDate = lastException?.endDate || defaultAvailability.endDate || defaultAvailability.startDate;
    const nextStartDate = getNextMonday(seedDate) || defaultAvailability.startDate;
    const startDate = nextStartDate && nextStartDate >= currentWeekStart ? nextStartDate : currentWeekStart;
    setExceptions((current) => [
      ...current,
      {
        id: `temp-${Date.now()}`,
        availabilityExceptionId: 0,
        startDate,
        endDate: getSunday(startDate),
        availableHoursPerWeek: '',
      },
    ]);
  };

  const updateDefaultStartDate = (value: string) => {
    const startDate = getMonday(value);
    setDefaultAvailability((current) => ({
      ...current,
      startDate,
      endDate: getSunday(startDate),
    }));
  };

  const updateDefaultEndDate = (value: string) => {
    setDefaultAvailability((current) => ({
      ...current,
      endDate: value,
    }));
  };

  const updateException = (id: string, updates: Partial<WeeklyAvailability>) => {
    setExceptions((current) =>
      current.map((exception) => {
        if (exception.id !== id) return exception;

        if (updates.startDate !== undefined) {
          const startDate = getMonday(updates.startDate);
          return {
            ...exception,
            startDate,
            endDate: getSunday(startDate),
          };
        }

        if (updates.endDate !== undefined) {
          return {
            ...exception,
            endDate: getSunday(updates.endDate),
          };
        }

        return {
          ...exception,
          ...updates,
          availableHoursPerWeek:
            updates.availableHoursPerWeek !== undefined
              ? normalizeHours(updates.availableHoursPerWeek)
              : exception.availableHoursPerWeek,
        };
      }),
    );
  };

  const handleSortExceptions = () => {
    const nextDirection = exceptionSortDirection === 'asc' ? 'desc' : 'asc';
    setExceptionSortDirection(nextDirection);

    setExceptions((current) =>
      [...current].sort((a, b) => {
        const aStart = getStartDateTime(a.startDate);
        const bStart = getStartDateTime(b.startDate);
        return nextDirection === 'asc' ? aStart - bStart : bStart - aStart;
      }),
    );
  };

  const buildAvailabilityPayload = () => {
    const availabilityTypeIds = availabilityForm.isAvailable ? availabilityForm.availabilityTypeIds : [];
    const defaultAvailableHoursPerWeek = Number(defaultAvailability.availableHoursPerWeek || 0);
    return {
      availabilityId: availabilityForm.availabilityId || 0,
      availabilityTypeId: availabilityTypeIds.length > 0 ? availabilityTypeIds[0] : 0,
      availabilityTypeIds,
      availabilityTypes: availabilityForm.isAvailable ? selectedAvailabilityTypes : [],
      availableHoursPerWeek: defaultAvailableHoursPerWeek,
      defaultAvailableHoursPerWeek,
      preferredTimeSlot: null,
      notes: null,
      isAvailable: availabilityForm.isAvailable,
      approvalStatus: availabilityForm.approvalStatus || 'Pending',
      exceptions: exceptions.map((exception) => ({
        availabilityExceptionId: exception.availabilityExceptionId || 0,
        startDate: exception.startDate,
        endDate: exception.endDate,
        availableHoursPerWeek: Number(exception.availableHoursPerWeek || 0),
      })),
    };
  };

  const availabilitySnapshot = useMemo(
    () =>
      JSON.stringify({
        availabilityForm,
        defaultAvailability,
        exceptions,
      }),
    [availabilityForm, defaultAvailability, exceptions],
  );

  const formSnapshot = useMemo(
    () =>
      JSON.stringify({
        firstName,
        lastName,
        profileImage,
        photoId,
        currentOrganization,
        location,
        itSpecialtyId,
        totalExperience,
        organizationCount,
        shortBio,
        selectedSkills,
        selectedTechnologies,
        availabilityForm,
        defaultAvailability,
        exceptions,
      }),
    [
      availabilityForm,
      currentOrganization,
      defaultAvailability,
      exceptions,
      firstName,
      itSpecialtyId,
      lastName,
      location,
      organizationCount,
      photoId,
      profileImage,
      selectedSkills,
      selectedTechnologies,
      shortBio,
      totalExperience,
    ],
  );

  useEffect(() => {
    if (!loading && initialSnapshot === null) {
      setInitialSnapshot(formSnapshot);
    }
  }, [formSnapshot, initialSnapshot, loading]);

  useEffect(() => {
    if (!loading && initialAvailabilitySnapshot === null) {
      setInitialAvailabilitySnapshot(availabilitySnapshot);
    }
  }, [availabilitySnapshot, initialAvailabilitySnapshot, loading]);

  const hasUnsavedChanges = useMemo(() => {
    if (loading || initialSnapshot === null) return false;
    return initialSnapshot !== formSnapshot;
  }, [formSnapshot, initialSnapshot, loading]);

  const hasAvailabilityChanges = useMemo(() => {
    if (loading || initialAvailabilitySnapshot === null) return false;
    return initialAvailabilitySnapshot !== availabilitySnapshot;
  }, [availabilitySnapshot, initialAvailabilitySnapshot, loading]);

  useEffect(() => {
    (globalThis as typeof globalThis & { __ISX_HAS_UNSAVED_CHANGES__?: boolean }).__ISX_HAS_UNSAVED_CHANGES__ = hasUnsavedChanges;
    return () => {
      (globalThis as typeof globalThis & { __ISX_HAS_UNSAVED_CHANGES__?: boolean }).__ISX_HAS_UNSAVED_CHANGES__ = false;
    };
  }, [hasUnsavedChanges]);

  const saveProfileData = useCallback(async (): Promise<boolean> => {
    setFormError('');

    if (Object.keys(errors).length > 0) {
      setFormError('Please fix the highlighted fields before saving.');
      return false;
    }

    try {
      setIsSaving(true);
      dispatch(
        updatePersonalInfo({
          firstName,
          lastName,
          profileImage: profileImage || undefined,
          profileImageFile,
        }),
      );
      dispatch(
        updateProfessionalInfo({
          itSpecialtyId: Number(itSpecialtyId),
          currentOranizationName: currentOrganization,
          location: {
            ...profile.professionalInfo.location,
            displayName: location,
          },
          totalExperience: totalExperience ? Number(totalExperience) : 0,
          organizationCount: organizationCount ? Number(organizationCount) : 0,
          availabilities: [...otherAvailabilitiesRef.current, buildAvailabilityPayload()],
        }),
      );
      dispatch(
        updateBackgroundInfo({
          shortBio,
          skills: selectedSkills,
          interests: selectedTechnologies,
        }),
      );

      await saveUserProfile(shortBio);
      setInitialSnapshot(formSnapshot);
      setInitialAvailabilitySnapshot(availabilitySnapshot);
      dispatch(setProfileIncomplete(false));
      return true;
    } catch (error: any) {
      setFormError(error?.message || 'Failed to save profile.');
      return false;
    } finally {
      setIsSaving(false);
    }
  }, [
    availabilitySnapshot,
    buildAvailabilityPayload,
    currentOrganization,
    dispatch,
    errors,
    firstName,
    formSnapshot,
    itSpecialtyId,
    lastName,
    location,
    organizationCount,
    profile.professionalInfo.location,
    profileImage,
    profileImageFile,
    saveUserProfile,
    selectedSkills,
    selectedTechnologies,
    shortBio,
    totalExperience,
  ]);

  const { isBlocked, requestNavigation, handleLeave, handleSaveAndLeave } = useUnsavedChangesGuard({
    when: hasUnsavedChanges && !isSaving,
    onSaveAndLeave: saveProfileData,
  });

  useEffect(() => {
    const handleSaveRequest = async (event: Event) => {
      const customEvent = event as CustomEvent<{ nextHref?: string }>;
      const nextHref = customEvent.detail?.nextHref;
      const didSave = await saveProfileData();
      if (didSave && nextHref) {
        navigate(nextHref);
      }
    };

    globalThis.addEventListener('isx-unsaved-save-request', handleSaveRequest);
    return () => globalThis.removeEventListener('isx-unsaved-save-request', handleSaveRequest);
  }, [navigate, saveProfileData]);

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    const didSave = await saveProfileData();
    if (didSave) {
      navigate('/my-profile');
    }
  };

  if (loading) {
    return <p className="py-8 text-center text-gray-600">Loading profile form...</p>;
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-5 pb-64 sm:pb-0">
      {/* ── Page Header ── */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <p className="mb-0 max-w-2xl text-sm leading-6 text-gray-600 sm:text-base" style={{ color: theme.text }}>Keep your professional details, skills, and availability current.</p>
        </div>
      </div>

      {formError && (
        <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {formError}
        </div>
      )}

      {/* ── Profile Photo + Basic Information ── */}
      <div className="grid grid-cols-1 gap-5 lg:grid-cols-[280px_1fr]">
        {/* Profile Photo */}
        <section className="rounded-lg border border-gray-200 bg-white p-5 shadow-sm">
          <div className="flex items-center justify-between gap-3"> 
            <h2 className="text-lg font-semibold text-gray-950" style={{ color: theme.text }}>Profile Photo</h2> 
            <button type="button" onClick={() => fileInputRef.current?.click()} className="text-xs font-medium text-brand-primary transition hover:text-brand-hover" > Change Photo </button> 
          </div>
          <div className="mx-auto mt-4 w-full max-w-[240px] lg:max-w-none">
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className="group relative block w-full overflow-hidden rounded-lg bg-gradient-to-b from-brand-primary to-brand-gray"
              style={{ aspectRatio: '1 / 1' }}
              aria-label="Upload profile photo"
            >
              {profileImage ? (
                <img src={profileImage} alt="" className="h-full w-full object-cover" />
              ) : photoId ? (
                <PhotoImage photoId={photoId} className="h-full w-full object-cover" />
              ) : (
                <div className="flex h-full w-full flex-col items-center justify-center gap-2 text-white">
                  <Camera className="h-10 w-10" />
                  <span className="text-sm text-white/80" style={{ color: theme.text }}>Click to upload photo</span>
                </div>
              )}
              <div className="absolute inset-0 flex items-center justify-center bg-black/30 opacity-0 transition-opacity group-hover:opacity-100">
                <div className="flex flex-col items-center gap-2 text-white">
                  <Camera className="h-8 w-8" />
                  <span className="text-sm font-semibold">Change Photo</span>
                </div>
              </div>
            </button>
          </div>
          <input ref={fileInputRef} type="file" accept="image/*" hidden onChange={handlePhotoChange} />
        </section>

        {/* Basic Information */}
        <section className="rounded-lg border border-gray-200 bg-white p-5 shadow-sm">
          <h2 className="text-lg font-semibold text-gray-950 " style={{ color: theme.text }}>Basic Information</h2>
          <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-gray-500" style={{ color: theme.text }}>First Name<RequiredMark /></label>
              <Input value={firstName} onChange={(event) => setFirstName(event.target.value)} className={fieldClass} />
              <FieldError message={errors.firstName} />
            </div>
            <div>
              <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-gray-500" style={{ color: theme.text }}>Last Name<RequiredMark /></label>
              <Input value={lastName} onChange={(event) => setLastName(event.target.value)} className={fieldClass} />
              <FieldError message={errors.lastName} />
            </div>
            <div>
              <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-gray-500" style={{ color: theme.text }}>Current Organization<RequiredMark /></label>
              <Input value={currentOrganization} maxLength={100} onChange={(event) => setCurrentOrganization(event.target.value)} className={fieldClass} />
              <FieldError message={errors.currentOrganization} />
            </div>
            <div>
              <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-gray-500" style={{ color: theme.text }}>Location<RequiredMark /></label>
              <Input value={location} onChange={(event) => setLocation(event.target.value)} className={fieldClass} />
              <FieldError message={errors.location} />
            </div>
          </div>
        </section>
      </div>

      {/* ── Professional Information ── */}
      <section className="rounded-lg border border-gray-200 bg-white p-5 shadow-sm">
        <h2 className="text-lg font-semibold"  style={{ color: theme.text }} >Professional Information</h2>
        <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          <div>
            <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-gray-500" style={{ color: theme.text }}>IT Specialty<RequiredMark /></label>
            <select value={itSpecialtyId} onChange={(event) => setItSpecialtyId(event.target.value)} className={fieldClass}>
              <option value="">Select specialty</option>
              {specialties.map((specialty) => (
                <option key={specialty.itSpecialtyId} value={specialty.itSpecialtyId}>
                  {specialty.itSpecialtyName}
                </option>
              ))}
            </select>
            <FieldError message={errors.itSpecialtyId} />
          </div>
          <div>
            <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-gray-500" style={{ color: theme.text }} ><RequiredMark />Total Experience (Years)</label>
            <Input type="number" min="0" value={totalExperience} onChange={(event) => setTotalExperience(event.target.value)} className={fieldClass} />
            <FieldError message={errors.totalExperience} />
          </div>
          <div>
            <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-gray-500" style={{ color: theme.text }} ><RequiredMark />Number of Companies Worked At</label>
            <Input type="number" min="0" value={organizationCount} onChange={(event) => setOrganizationCount(event.target.value)} className={fieldClass} />
            <FieldError message={errors.organizationCount} />
          </div>
        </div>
      </section>

      {/* ── About Me ── */}
      <section className="rounded-lg border border-gray-200 bg-white p-5 shadow-sm">
        <h2 className="text-lg font-semibold text-gray-950" style={{ color: theme.text }}>About Me</h2>
        <div className="mt-4">
          <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-gray-500" style={{ color: theme.text }}>Tell Us About Yourself</label>
          <Textarea
            value={shortBio}
            maxLength={500}
            onChange={(event) => setShortBio(event.target.value)}
            className={`${fieldClass} min-h-28 resize-y`}
            placeholder="Write a short bio about yourself..."
          />
          <p className="mt-1.5 text-right text-xs text-gray-400">{shortBio.length} / 500</p>
        </div>
      </section>

      {/* ── Skills & Technologies ── */}
      <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
        {/* Skills */}
        <section className="rounded-lg border border-gray-200 bg-white p-5 shadow-sm">
          <h2 className="text-lg font-semibold text-gray-950" style={{ color: theme.text }}>Skills</h2>
          <div className="mt-4">
            <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-gray-500" style={{ color: theme.text }}>IT Skills<RequiredMark /></label>
            <select value="" onChange={(event) => toggleSkill(event.target.value)} className={fieldClass}>
              <option value="">Add a skill...</option>
              {skills.map((skill) => (
                <option key={skill.skillId} value={skill.skillId}>
                  {selectedSkills.some((item) => item.skillId === skill.skillId) ? '✓ ' : ''}
                  {skill.skillName}
                </option>
              ))}
            </select>
            <FieldError message={errors.selectedSkills} />
          </div>
          <div className="mt-4 space-y-2">
            {selectedSkills.length === 0 ? (
              <p className="text-sm text-gray-500" style={{ color: theme.text }}>No skills added yet.</p>
            ) : (
              selectedSkills.map((skill) => {
                const isRejected = String(skill.approvalStatus).toLowerCase() === 'rejected';

                return (
                  <div
                    key={skill.skillId}
                    className="rounded-lg border border-blue-100 bg-blue-50 px-3 py-2"
                  >
                    <div className="flex items-center gap-3">
                      <span className="min-w-0 flex-1 truncate text-sm font-medium text-blue-700">{skill.skillName}</span>
                      {skill.approvalStatus && <ApprovalStatusBadge status={skill.approvalStatus} />}
                      {isRejected && skill.rejectionReason && (
                        <button
                          type="button"
                          onClick={() => setRejectionReasonModalSkill(skill)}
                          className="rounded-md border border-blue-200 px-2 py-1 text-xs font-medium text-blue-700 hover:bg-white"
                        >
                          View
                        </button>
                      )}
                      <Input
                        type="number"
                        min="0"
                        value={skill.experience || ''}
                        onChange={(event) => updateSkillExperience(skill.skillId, event.target.value)}
                        className="h-8 w-20 rounded-md border-blue-200 bg-white text-sm"
                        placeholder="Yrs"
                      />
                       <FieldError message={errors[skill.skillId]} />
                      <button
                        type="button"
                        onClick={() => setSelectedSkills((current) => current.filter((item) => item.skillId !== skill.skillId))}
                        className="rounded-md p-1 text-blue-400 hover:bg-white hover:text-red-600"
                        aria-label={`Remove ${skill.skillName}`}
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </section>

        {/* Technologies */}
        <section className="rounded-lg border border-gray-200 bg-white p-5 shadow-sm">
          <h2 className="text-lg font-semibold text-gray-950" style={{ color: theme.text }}>Technologies to Explore</h2>
          <div className="mt-4">
            <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-gray-500">Technologies I Want To Explore<RequiredMark /></label>
            <select value="" onChange={(event) => toggleTechnology(event.target.value)} className={fieldClass}>
              <option value="">Add a technology...</option>
              {technologies.map((technology) => (
                <option key={technology.interestId} value={technology.interestId}>
                  {selectedTechnologies.some((item) => item.interestId === technology.interestId) ? '✓ ' : ''}
                  {technology.interestName}
                </option>
              ))}
            </select>
            <FieldError message={errors.selectedTechnologies} />
          </div>
          <div className="mt-4 space-y-2">
            {selectedTechnologies.length === 0 ? (
              <p className="text-sm text-gray-500" style={{ color: theme.text }}>No technologies added yet.</p>
            ) : (
              selectedTechnologies.map((technology) => (
                <div key={technology.interestId} className="rounded-lg border border-emerald-100 bg-emerald-50 px-3 py-2">
                  <div className="flex items-center gap-3">
                    <span className="min-w-0 flex-1 truncate text-sm font-medium text-emerald-700">{technology.interestName}</span>
                    <button
                      type="button"
                      onClick={() =>
                        setSelectedTechnologies((current) => current.filter((item) => item.interestId !== technology.interestId))
                      }
                      className="rounded-md p-1 text-emerald-400 hover:bg-white hover:text-red-600"
                      aria-label={`Remove ${technology.interestName}`}
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                  {technology.interestName.toLowerCase() === 'other' && (
                    <Input
                      value={technology.customExploreTechnologies || ''}
                      onChange={(event) =>
                        setSelectedTechnologies((current) =>
                          current.map((item) =>
                            item.interestId === technology.interestId
                              ? { ...item, customExploreTechnologies: event.target.value }
                              : item,
                          ),
                        )
                      }
                      className="mt-2 h-8 rounded-md border-emerald-200 bg-white text-sm"
                      placeholder="Enter other technology"
                    />
                  )}
                </div>
              ))
            )}
          </div>
        </section>
      </div>

      {/* ── Availability ── */}
      <section
        id="availability-section"
        tabIndex={-1}
        className={`rounded-lg border bg-white p-5 shadow-sm outline-none transition ${
          availabilityFocused ? 'border-brand-primary ring-2 ring-brand-primary/20' : 'border-gray-200'
        }`}
      >
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <h2 className="text-lg font-semibold text-gray-950"style={{ color: theme.text }}>Default Availability</h2>
          <div className="flex flex-wrap gap-4">
            {[
              { label: 'Available', value: true },
              { label: 'Not Available', value: false },
            ].map((option) => (
              <label key={option.label} className="inline-flex cursor-pointer items-center gap-2 text-sm font-semibold text-gray-700">
                <input
                  type="radio"
                  name="availabilityStatus"
                  checked={availabilityForm.isAvailable === option.value}
                  onChange={() => setAvailabilityStatus(option.value)}
                  className="h-4 w-4 accent-brand-primary"
                />
                <span className={option.value ? 'text-green-700' : 'text-orange-600'}>{option.label}</span>
              </label>
            ))}
          </div>
        </div>

        {/* Default Availability Hours */}
        <div className="mt-5">
          <div className="mt-3 grid grid-cols-1 gap-4 sm:grid-cols-3">
            <div>
              <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-gray-500">Hours Per Week{availabilityForm.isAvailable && <RequiredMark />}</label>
              <Input
                type="number"
                min="0"
                max="40"
                value={defaultAvailability.availableHoursPerWeek}
                disabled={!availabilityForm.isAvailable}
                onChange={(event) =>
                  setDefaultAvailability((current) => ({
                    ...current,
                    availableHoursPerWeek: normalizeHours(event.target.value),
                  }))
                }
                className={fieldClass}
              />
              <FieldError message={errors.defaultHours} />
            </div>
          </div>
        </div>

        {/* Available For */}
        {availabilityForm.isAvailable && (
          <div className="mt-5">
            <h3 className="text-sm font-semibold uppercase tracking-wide text-gray-600" style={{ color: theme.text }}>Available For</h3>
            <div className="mt-3 flex flex-wrap gap-2">
              {availabilityTypes.map((availabilityType) => {
                const selected = availabilityForm.availabilityTypeIds.includes(availabilityType.availabilityTypeId);
                return (
                  <button
                    key={availabilityType.availabilityTypeId}
                    type="button"
                    onClick={() => toggleAvailabilityType(availabilityType.availabilityTypeId)}
                    className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-sm font-semibold transition ${
                      selected
                        ? 'bg-brand-primary text-white ring-1 ring-brand-primary'
                        : 'bg-slate-100 text-slate-700 ring-1 ring-slate-200 hover:bg-slate-200'
                    }`}
                  >
                    {selected && <Check className="h-3.5 w-3.5" />}
                    {availabilityType.availabilityTypeName}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Exceptions */}
        {availabilityForm.isAvailable && (
          <div className="mt-5">
            <div className="flex flex-col items-stretch gap-3 sm:flex-row sm:items-center sm:justify-between"> 
              <h3 className="text-sm font-semibold uppercase tracking-wide text-gray-600">Exceptions</h3> 
              <div className="flex flex-wrap items-center gap-2"> 
                <button type="button" onClick={handleSortExceptions} className="inline-flex items-center gap-2 rounded-lg border border-gray-300 px-3 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50" > 
                  <ArrowUpDown className="h-4 w-4" /> Sort {exceptionSortDirection === 'asc' ? '(Asc)' : '(Desc)'} 
                </button> 
                <button type="button" onClick={addException} className="inline-flex items-center gap-2 rounded-lg border border-brand-primary px-3 py-2 text-sm font-semibold text-brand-primary hover:bg-brand-primary/5" > 
                  <Plus className="h-4 w-4" /> Add Exception 
                </button> 
              </div> 
            </div>
            <FieldError message={errors.exceptions} />
            <div className="mt-3 space-y-3">
              {activeExceptions.map((exception) => (
                <div
                  key={exception.id}
                  className="grid grid-cols-1 gap-3 rounded-lg border border-gray-200 bg-gray-50 p-4 sm:grid-cols-2 lg:grid-cols-[1fr_1fr_1fr_auto]"
                >
                  <div>
                    <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-gray-500">Start Date<RequiredMark /></label>
                    <WeekRangeDatePicker
                      value={exception.startDate}
                      min={currentWeekStart}
                      onChange={(value) => updateException(exception.id, { startDate: value })}
                      rangeStart={exception.startDate}
                      rangeEnd={
                        exception.endDate && exception.endDate !== getSunday(exception.startDate)
                          ? exception.endDate
                          : getSunday(exception.startDate)
                      }
                      className={getInputClassName({
                        hasError: Boolean(errors[`exception-${exception.id}-startDate`]),
                        startDate: exception.startDate,
                        endDate: exception.endDate,
                      })}
                    />
                    <FieldError message={errors[`exception-${exception.id}-startDate`]} />
                  </div>
                  <div>
                    <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-gray-500">End Date<RequiredMark /></label>
                    <WeekRangeDatePicker
                      value={exception.endDate}
                      min={exception.startDate || '1970-01-04'}
                      onChange={(value) => updateException(exception.id, { endDate: value })}
                      rangeStart={exception.startDate}
                      rangeEnd={
                        exception.endDate && exception.endDate !== getSunday(exception.startDate)
                          ? exception.endDate
                          : getSunday(exception.startDate)
                      }
                      className={getInputClassName({
                        hasError: Boolean(errors[`exception-${exception.id}-endDate`]),
                        startDate: exception.startDate,
                        endDate: exception.endDate,
                        isEndField: true,
                      })}
                    />
                    <FieldError message={errors[`exception-${exception.id}-endDate`]} />
                  </div>
                  <div>
                    <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-gray-500">Hours Per Week<RequiredMark /></label>
                    <Input
                      type="number"
                      min="0"
                      max="40"
                      value={exception.availableHoursPerWeek}
                      onChange={(event) => updateException(exception.id, { availableHoursPerWeek: event.target.value })}
                      className={fieldClass}
                    />
                    <FieldError message={errors[`exception-${exception.id}-hours`]} />
                  </div>
                  <div className="flex items-end justify-end lg:justify-center">
                    <button
                      type="button"
                      onClick={() => setExceptions((current) => current.filter((item) => item.id !== exception.id))}
                      className="flex h-10 w-10 items-center justify-center rounded-lg text-gray-400 hover:bg-white hover:text-red-600"
                      aria-label="Delete exception"
                    >
                      <Trash2 className="h-5 w-5" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </section>

      {/* ── Sticky Save Bar ── */}
      <div className="flex justify-end gap-2 border-t border-gray-200 bg-white/95 py-3 pr-5 backdrop-blur sm:sticky sm:bottom-0">
        <button
          type="button"
          onClick={() => requestNavigation(() => navigate(-1))}
          className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={isSaving || !isFormValid || !hasUnsavedChanges}
          title={
            !isFormValid
              ? 'Please complete all mandatory fields before saving.'
              : !hasUnsavedChanges
                ? 'Make a profile or availability change to enable saving.'
                : undefined
          }
          className="inline-flex items-center gap-2 rounded-lg bg-brand-primary px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-brand-hover disabled:cursor-not-allowed disabled:bg-gray-300"
        >
          <Save className="h-4 w-4" />
          {isSaving ? 'Saving...' : 'Save Profile'}
        </button>
      </div>
      {toast && <Toast message={toast.message} type={toast.type} onClose={clearToast} />}
      <UnsavedChangesDialog
        isOpen={isBlocked}
        onConfirm={handleLeave}
        onSave={handleSaveAndLeave}
      />
      {rejectionReasonModalSkill && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
          <div className="w-full max-w-sm rounded-lg bg-white shadow-xl">
            <div className="flex items-center justify-between border-b border-gray-200 p-5">
              <div className="min-w-0 flex-1">
                <h2 className="text-lg font-bold text-gray-900">Skill Rejected</h2>
                <p className="mt-0.5 truncate text-xs text-gray-500">{rejectionReasonModalSkill.skillName}</p>
              </div>
              <button
                type="button"
                onClick={() => setRejectionReasonModalSkill(null)}
                className="flex-shrink-0 rounded-full p-1 transition-colors hover:bg-gray-100"
                aria-label="Close"
              >
                <X className="h-5 w-5 text-gray-500" />
              </button>
            </div>
            <div className="p-5">
              <p className="text-sm text-gray-700">{rejectionReasonModalSkill.rejectionReason}</p>
            </div>
          </div>
        </div>
      )}
    </form>
  );
}
